// src/utils/departmentApi.js
import { api } from './api';
import API_ENDPOINTS from '../config/apiConfig';

// Cache for departments per orgId
const departmentCache = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Get departments by organization ID
export const getDepartmentsByOrgId = async (orgId) => {
  if (!orgId) {
    console.error('orgId is required for getDepartmentsByOrgId');
    return [];
  }

  // Check cache
  const cacheKey = `org_${orgId}`;
  const cachedData = departmentCache[cacheKey];

  if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {

    return cachedData.data;
  }

  try {
    const response = await api.get(API_ENDPOINTS.GET_DEPARTMENTS_BY_ORG(orgId));
    const departments = response.data || [];

    // Update cache
    departmentCache[cacheKey] = {
      data: departments,
      timestamp: Date.now()
    };

    return departments;
  } catch (error) {
    console.error(`Error fetching departments for org ${orgId}:`, error);

    // Return cached data if available, even if expired
    if (cachedData) {

      return cachedData.data;
    }

    return [];
  }
};

// Get all departments (filtered by user's access)
export const getAllDepartments = async () => {
  const cacheKey = 'all_departments';
  const cachedData = departmentCache[cacheKey];

  if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {

    return cachedData.data;
  }

  try {
    const response = await api.get(API_ENDPOINTS.DEPARTMENTS);
    const departments = response.data || [];

    // Update cache
    departmentCache[cacheKey] = {
      data: departments,
      timestamp: Date.now()
    };

    return departments;
  } catch (error) {
    console.error('Error fetching all departments:', error);

    // Return cached data if available, even if expired
    if (cachedData) {

      return cachedData.data;
    }

    return [];
  }
};

// Create a new department
export const createDepartment = async (departmentData) => {
  try {
    const response = await api.post(API_ENDPOINTS.DEPARTMENTS, departmentData);

    // Invalidate cache for the organization
    const cacheKey = `org_${departmentData.orgId}`;
    delete departmentCache[cacheKey];
    delete departmentCache['all_departments'];

    return response.data;
  } catch (error) {
    console.error('Error creating department:', error);
    throw error;
  }
};

// Update a department
export const updateDepartment = async (departmentId, departmentData) => {
  try {
    const response = await api.put(`${API_ENDPOINTS.DEPARTMENTS}?id=${departmentId}`, departmentData);

    // Invalidate all caches
    Object.keys(departmentCache).forEach(key => delete departmentCache[key]);

    return response.data;
  } catch (error) {
    console.error(`Error updating department ${departmentId}:`, error);
    throw error;
  }
};

// Delete a department (soft delete)
export const deleteDepartment = async (departmentId) => {
  try {
    await api.delete(`${API_ENDPOINTS.DEPARTMENTS}?id=${departmentId}`);

    // Invalidate all caches
    Object.keys(departmentCache).forEach(key => delete departmentCache[key]);

    return true;
  } catch (error) {
    console.error(`Error deleting department ${departmentId}:`, error);
    throw error;
  }
};

// Clear the department cache (useful when user changes organization)
export const clearDepartmentCache = () => {
  Object.keys(departmentCache).forEach(key => delete departmentCache[key]);
};

// ===== Department Chart of Account Mappings =====

// Get department COA mappings for a location
export const getDepartmentCoaMappings = async (orgId, locationId) => {
  if (!orgId || !locationId) {
    console.error('orgId and locationId are required for getDepartmentCoaMappings');
    return [];
  }

  try {
    const response = await api.get(`${API_ENDPOINTS.DEPARTMENT_COA_MAPPINGS}?orgId=${orgId}&locationId=${locationId}`);
    return response.data || [];
  } catch (error) {
    console.error(`Error fetching department COA mappings for org ${orgId}, location ${locationId}:`, error);
    return [];
  }
};

// Create a department COA mapping
export const createDepartmentCoaMapping = async (mappingData) => {
  try {
    const response = await api.post(API_ENDPOINTS.DEPARTMENT_COA_MAPPINGS, mappingData);
    return response.data;
  } catch (error) {
    console.error('Error creating department COA mapping:', error);
    throw error;
  }
};

// Update a department COA mapping
export const updateDepartmentCoaMapping = async (id, chartOfAccountId) => {
  try {
    const response = await api.put(`${API_ENDPOINTS.DEPARTMENT_COA_MAPPINGS}?id=${id}`, { chartOfAccountId });
    return response.data;
  } catch (error) {
    console.error(`Error updating department COA mapping ${id}:`, error);
    throw error;
  }
};

// Delete a department COA mapping
export const deleteDepartmentCoaMapping = async (id) => {
  try {
    await api.delete(`${API_ENDPOINTS.DEPARTMENT_COA_MAPPINGS}?id=${id}`);
    return true;
  } catch (error) {
    console.error(`Error deleting department COA mapping ${id}:`, error);
    throw error;
  }
};

// ===== Labor Chart of Account Mappings =====

// Get labor COA mappings for a location (maps labor positions to GL expense accounts)
export const getLaborCoaMappings = async (orgId, locationId) => {
  if (!orgId || !locationId) {
    console.error('orgId and locationId are required for getLaborCoaMappings');
    return [];
  }

  try {
    const response = await api.get(`${API_ENDPOINTS.LABOR_COA_MAPPINGS}?orgId=${orgId}&locationId=${locationId}`);
    return response.data || [];
  } catch (error) {
    console.error(`Error fetching labor COA mappings for org ${orgId}, location ${locationId}:`, error);
    return [];
  }
};

// Create a labor COA mapping
export const createLaborCoaMapping = async (mappingData) => {
  try {
    const response = await api.post(API_ENDPOINTS.LABOR_COA_MAPPINGS, mappingData);
    return response.data;
  } catch (error) {
    console.error('Error creating labor COA mapping:', error);
    throw error;
  }
};

// Update a labor COA mapping
export const updateLaborCoaMapping = async (id, chartOfAccountId) => {
  try {
    const response = await api.put(`${API_ENDPOINTS.LABOR_COA_MAPPINGS}?id=${id}`, { chartOfAccountId });
    return response.data;
  } catch (error) {
    console.error(`Error updating labor COA mapping ${id}:`, error);
    throw error;
  }
};

// Delete a labor COA mapping
export const deleteLaborCoaMapping = async (id) => {
  try {
    await api.delete(`${API_ENDPOINTS.LABOR_COA_MAPPINGS}?id=${id}`);
    return true;
  } catch (error) {
    console.error(`Error deleting labor COA mapping ${id}:`, error);
    throw error;
  }
};

// Bulk upsert labor COA mappings (create or update in a single request)
export const bulkUpsertLaborCoaMappings = async (orgId, locationId, mappings) => {
  if (!orgId || !locationId) {
    throw new Error('orgId and locationId are required for bulk upsert');
  }
  if (!mappings || mappings.length === 0) {
    throw new Error('No mappings provided');
  }

  try {
    const response = await api.post(API_ENDPOINTS.LABOR_COA_MAPPINGS, {
      orgId,
      locationId,
      mappings // Array of { positionId, chartOfAccountId }
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk upserting labor COA mappings:', error);
    throw error;
  }
};

// ===== AI Position to GL Account Mapping =====

/**
 * Use AI to suggest position-to-GL account mappings
 * @param {Object} params - The mapping parameters
 * @param {number} params.orgId - Organization ID
 * @param {number} params.locationId - Location ID
 * @param {Array} params.positions - Array of positions to map
 * @param {Array} params.glAccounts - Array of available GL accounts
 * @returns {Promise<Object>} - AI mapping suggestions
 * Note: the caller decides WHICH positions to send (already-mapped filtering
 * happens client-side); the route maps whatever it receives.
 */
export const getAiPositionGlMappingSuggestions = async ({ orgId, locationId, positions, glAccounts }) => {
  if (!orgId || !locationId) {
    console.error('orgId and locationId are required for AI position GL mapping');
    throw new Error('orgId and locationId are required');
  }

  if (!positions || positions.length === 0) {
    throw new Error('No positions provided for mapping');
  }

  if (!glAccounts || glAccounts.length === 0) {
    throw new Error('No GL accounts provided for mapping');
  }

  try {
    const response = await api.post(API_ENDPOINTS.AI_POSITION_GL_MAPPING, {
      orgId,
      locationId,
      positions,
      glAccounts
    });
    return response.data;
  } catch (error) {
    console.error('Error getting AI position GL mapping suggestions:', error);
    throw error;
  }
};